import type { PartnerVariant } from "@/types/LoginInfo";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

/**
 * STIC-16004: one label per partner variant, shared by the partner table, the
 * partner form, the quote pickers and the organisation selector. Must match
 * the backend's PartnerServiceImpl.getSuggestionPartnerVariant, which renders
 * the same keys for the column filter.
 *
 * One label per variant; the key order is the order the forms offer them in.
 */
const LABEL_KEY: Record<PartnerVariant, LanguageKey> = {
  CHANNEL_PARTNER: LanguageKey.partner_variant_channel_partner_key,
  MULTI_ORG: LanguageKey.partner_variant_multi_org_key,
  MULTI_ORG_INDIRECT: LanguageKey.partner_variant_multi_org_indirect_key,
  SENSOLUS: LanguageKey.partner_variant_sensolus_key,
};

const PARTNER_VARIANTS = Object.keys(LABEL_KEY) as PartnerVariant[];

/** Translated label, or `fallback` for an unknown or missing variant. */
export const partnerVariantLabel = (variant?: string | null, fallback = ""): string => {
  const key = variant ? LABEL_KEY[variant as PartnerVariant] : undefined;
  return key ? translate(key) : fallback;
};

/**
 * The same variants, short enough for a badge. The full labels carry a
 * parenthetical that a pill in the organisation selector clips (STIC-16004).
 */
const SHORT_LABEL_KEY: Record<PartnerVariant, LanguageKey> = {
  CHANNEL_PARTNER: LanguageKey.partner_variant_channel_partner_key,
  MULTI_ORG: LanguageKey.partner_variant_multi_org_short_key,
  MULTI_ORG_INDIRECT: LanguageKey.partner_variant_multi_org_indirect_short_key,
  SENSOLUS: LanguageKey.partner_variant_sensolus_key,
};

/** Badge-sized label: no parenthetical. Use it wherever the width is fixed. */
export const partnerVariantShortLabel = (variant?: string | null, fallback = ""): string => {
  const key = variant ? SHORT_LABEL_KEY[variant as PartnerVariant] : undefined;
  return key ? translate(key) : fallback;
};

/** What this variant means for the partner's business, one line per variant. */
const DESCRIPTION_KEY: Record<PartnerVariant, LanguageKey> = {
  CHANNEL_PARTNER: LanguageKey.partner_variant_channel_partner_description_key,
  MULTI_ORG: LanguageKey.partner_variant_multi_org_description_key,
  MULTI_ORG_INDIRECT: LanguageKey.partner_variant_multi_org_indirect_description_key,
  SENSOLUS: LanguageKey.partner_variant_sensolus_description_key,
};

/** The description of the selected variant, empty for an unknown or missing one. */
export const partnerVariantDescription = (variant?: string | null): string => {
  const key = variant ? DESCRIPTION_KEY[variant as PartnerVariant] : undefined;
  return key ? translate(key) : "";
};

/** Radio / select options for the partner forms. */
export const partnerVariantOptions = (): { value: PartnerVariant; label: string }[] =>
  PARTNER_VARIANTS.map((value) => ({ value, label: partnerVariantLabel(value) }));

/** Every variant but the channel partner: no Salesforce link, no commercial override. */
export const isMultiOrgLikeVariant = (variant?: string | null): boolean =>
  !!variant && variant !== "CHANNEL_PARTNER";
